<template>
  <main class="camera">
    <Transition name="slide" mode="out-in">
      <div class="wrapper" v-if="initialRedirectDone">
        <div :class="['content', isIframed ? 'iframed' : '']">
          <QuestionInfo v-if="questionInfo.text" />
          <Mobile v-if="isMobileDevice" :uploadVideo="handleUploadVideo" />
          <Desktop v-else :uploadVideo="handleUploadVideo" />
        </div>
      </div>
    </Transition>
  </main>
</template>

<script lang="ts">
import Desktop from '@/components/Desktop.vue';
import Mobile from '@/components/Mobile.vue';
import QuestionInfo from '@/components/QuestionInfo.vue';
import { PostVideoToAltru, PutVideoToSignedUrl, SharedSignedUrl } from '@/store/videos/types';
import { MetaSourceProxy, useMeta } from 'vue-meta';
import { computed, Ref } from 'vue';
import { useStore } from 'vuex';
import { ToggleLoading } from '@/store/ui/types';
import { useI18n } from 'vue-i18n';
import { Answer, Client, Identity, Question, User } from 'altru-types';
import { useRouter } from 'vue-router';
import Bowser from 'bowser';
import { sendRecordingUploadedMessage } from '@/iframe-messaging';
import { UserInformation } from '@/types/user-data';

export default {
  name: 'Camera',
  components: {
    Mobile,
    Desktop,
    QuestionInfo,
  },
  setup(): {
    meta: MetaSourceProxy;
    isMobileDevice: Ref<boolean>;
    handleUploadVideo: (video: File) => void;
    questionInfo: Ref<Question>;
    userInformationNeeded: Ref<boolean>;
    isIframed: Ref<boolean>;
    initialRedirectDone: Ref<boolean>;
  } {
    const { t } = useI18n();
    const { getters, dispatch } = useStore();
    const { push } = useRouter();

    const userInformationNeeded: Ref<boolean> = computed(() => getters['clients/userInformationNeeded']);
    const userInformationCollected: Ref<UserInformation | null> = computed(
      () => getters['users/userInformationCollected'],
    );
    const isIframed: Ref<boolean> = computed(() => getters['status/isIframed']);

    const questionInfo: Ref<Question> = computed(() => getters['questions/questionInfo']);
    const payloadValue: Ref<string> = computed(() => getters['auth/payloadValue']);
    const responseConfigUrl: Ref<string> = computed(() => getters['videos/configUrl']);
    const workspaceInfo: Ref<Client | null> = computed(() => getters['clients/workspaceInfo']);
    const payloadAnswer: Ref<Answer | null> = computed(() => getters['answers/payloadAnswerInfo']);
    const credentials: Ref<Identity> = computed(() => getters['auth/credentialsValue']);
    const workspaceTermsAcceptedAt: Ref<string | null> = computed(() => getters['clients/workspaceTermsAcceptedAt']);
    const initialRedirectDone: Ref<boolean> = computed(() => getters['status/initialRedirectDone']);

    const metaInfoTitle = computed(() => t('CAMERA META'));
    const { meta } = useMeta({
      title: metaInfoTitle.value,
    });

    /* Upload file methods from store */
    const storeGetSignedUrl: ({ videoName, videoType, region }: SharedSignedUrl) => Promise<string> = async ({
      videoName,
      videoType,
      region,
    }) => {
      if (isIframed.value) {
        const response = await dispatch('videos/getSignedUrlWithCredentials', {
          videoName,
          videoType,
          region,
          headers: credentials.value,
        });
        if (response.success) {
          return response.data.url;
        } else {
          await dispatch('ui/setErrorMessage', { value: response.message });
          return await storeToggleLoading({ value: false, loadingTerm: '' });
        }
      }
      if (payloadValue.value) {
        const response = await dispatch('videos/getSignedUrlWithPayload', {
          videoName,
          videoType,
          region,
          payload: payloadValue.value,
        });
        if (response.success) {
          return response.data.url;
        } else {
          await dispatch('ui/setErrorMessage', { value: response.message });
          return await storeToggleLoading({ value: false, loadingTerm: '' });
        }
      }
    };

    const storePutVideoToSignedUrl: PutVideoToSignedUrl = async ({ signedUrl, video }) => {
      const response = await dispatch('videos/putVideoToSignedUrl', { signedUrl, video });

      if (response.status === 200) {
        const url = response.config.url;
        const cdnUrl = url.replace('https://altru-api.s3.amazonaws.com', 'https://cdn.altrulabs.com').split('?')[0];
        await dispatch('videos/storeConfigUrl', cdnUrl);
        return cdnUrl;
      } else {
        await dispatch('ui/setErrorMessage', { value: response.message ?? t('AWS ERROR UPLOAD') });
        await storeToggleLoading({ value: false, loadingTerm: '' });
      }
    };

    const storePostVideoToAltru: PostVideoToAltru = async ({ payload, region }) => {
      return await dispatch('videos/postVideoToAltru', { payload, region });
    };

    /* Ui methods from store */
    const storeToggleLoading: ToggleLoading = async ({ value, loadingTerm }) => {
      return await dispatch('ui/toggleLoading', { value, loadingTerm });
    };

    /* Check if device being used is mobile or desktop */
    const isMobileDevice = computed(() => {
      return Bowser.parse(window.navigator.userAgent).platform.type === 'mobile';
    });

    const handleUploadVideo = async (video: File): Promise<void> => {
      await dispatch('videos/saveVideoToStore', video);
      if (workspaceTermsAcceptedAt.value) {
        try {
          await dispatch('ui/toggleLoading', {
            value: true,
            loadingTerm: t('UPLOADING VIDEO'),
          });
          if (!responseConfigUrl.value) {
            /* Get signed URL */
            const signedUrl = await storeGetSignedUrl({
              videoName: video.name,
              videoType: video.type,
              region: workspaceInfo.value?.global_region ?? 'us',
            });
            if (signedUrl) {
              /* PUT video data */
              const responseUrl = await storePutVideoToSignedUrl({ signedUrl, video });
              if (responseUrl) {
                await handleFinishUpload(responseUrl);
              }
            }
          } else {
            await handleFinishUpload(responseConfigUrl.value);
          }
        } catch (e) {
          await storeToggleLoading({ value: false, loadingTerm: '' });
          console.error(e);
        }
      } else {
        await dispatch('ui/setErrorMessage', { value: t('TERMS AND CONDITIONS REQUIRED'), variant: 'warn' });
      }
    };

    const handleFinishUpload = async (cdnUrl: string) => {
      const user: null | Partial<Pick<User, 'first_name' | 'last_name' | 'email' | 'job_title'>> =
        userInformationCollected.value && {
          ...(userInformationCollected.value.firstName && { first_name: userInformationCollected.value.firstName }),
          ...(userInformationCollected.value.lastName && { last_name: userInformationCollected.value.lastName }),
          ...(userInformationCollected.value.email && { email: userInformationCollected.value.email }),
          ...(userInformationCollected.value.jobTitle && { job_title: userInformationCollected.value.jobTitle }),
        };

      const postVideoPayload = JSON.stringify({
        payload: payloadValue.value,
        question_id: +questionInfo.value.id,
        video_url: cdnUrl,
        status: 'pending_approval',
        user: {
          ...user,
          ...(workspaceTermsAcceptedAt.value && { terms_accepted_at: workspaceTermsAcceptedAt.value }),
        },
      });

      const globalRegion = workspaceInfo.value?.global_region ?? 'us';
      const response = await storePostVideoToAltru({
        payload: postVideoPayload,
        region: globalRegion,
      });
      if (response.success) {
        ((mixpanel as any)[globalRegion] as Mixpanel | undefined)?.track('app-free-upload');
        if (isIframed.value && payloadAnswer.value) {
          sendRecordingUploadedMessage({
            kind: 'answer recording uploaded',
            answerId: payloadAnswer.value.id ?? response.data?.id,
          });
        }

        const { data } = response;
        await dispatch('answers/addAnswerInfo', data);
        await storeToggleLoading({ value: false, loadingTerm: '' });
        await dispatch('videos/finishSession');
        await push('/');
      } else {
        if (response.message?.toLowerCase()?.includes('email is already in use')) {
          await dispatch('ui/setErrorMessage', {
            value: t('EXISTING EMAIL UPLOAD'),
            variant: 'error',
            persistent: true,
          });
        } else {
          await dispatch('ui/setErrorMessage', { value: response.message });
        }
        await storeToggleLoading({ value: false, loadingTerm: '' });
      }
    };

    return {
      meta,
      isMobileDevice,
      handleUploadVideo,
      questionInfo,
      userInformationNeeded,
      isIframed,
      initialRedirectDone,
    };
  },
};
</script>

<style lang="scss" scoped>
.camera {
  display: flex;
  flex-direction: column;
  flex: 1;
  align-items: center;

  .content {
    display: flex;
    flex-direction: column;
    height: 100%;
    gap: 24px;
    margin: 24px 0;

    &.iframed {
      margin: 0 0 24px;
    }
  }
}

.slide-enter-active {
  transition: all 0.15s ease-in;
}

.slide-leave-active {
  transition: all 0.3s ease-out;
}

.slide-enter-from {
  opacity: 0;
}

.slide-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}
</style>
